import { createContext } from "preact";
import {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "preact/hooks";
import { buildAssetUrl } from "../../pixi/assetsInit";
import { useAudio } from "../hooks/useAudio";

interface AudioContextType {
  isMusicEnabled: boolean;
  setMusicEnabled: (_enabled: boolean) => void;
  isSoundEnabled: boolean;
  setSoundEnabled: (_enabled: boolean) => void;
  playClickSound: () => void;
}

const AudioContext = createContext<AudioContextType | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useAudioContext() {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error("useAudioContext must be used within AudioProvider");
  }
  return context;
}

interface AudioProviderProps {
  children: preact.ComponentChildren;
}
export function AudioProvider({ children }: AudioProviderProps) {
  const [isMusicEnabled, setIsMusicEnabled] = useState(() => {
    const saved = localStorage.getItem("music-enabled");
    return saved !== null ? JSON.parse(saved) : false;
  });

  const [isSoundEnabled, setIsSoundEnabled] = useState(() => {
    const saved = localStorage.getItem("sound-enabled");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const isMusicEnabledRef = useRef(isMusicEnabled);

  useEffect(() => {
    isMusicEnabledRef.current = isMusicEnabled;
  }, [isMusicEnabled]);

  const backgroundMusic = useAudio(buildAssetUrl("/audio/baraban.mp3"), {
    volume: 0.3,
    loop: true,
  });

  const clickSound = useAudio(buildAssetUrl("/audio/crash.mp3"), {
    volume: 0.5,
  });

  const setMusicEnabled = useCallback((enabled: boolean) => {
    setIsMusicEnabled(enabled);
  }, []);

  const setSoundEnabled = useCallback((enabled: boolean) => {
    setIsSoundEnabled(enabled);
  }, []);

  const playClickSound = useCallback(() => {
    if (isSoundEnabled && clickSound.isLoaded) {
      clickSound.stop();
      clickSound.play();
    }
  }, [isSoundEnabled, clickSound]);

  useEffect(() => {
    localStorage.setItem("music-enabled", JSON.stringify(isMusicEnabled));
  }, [isMusicEnabled]);

  useEffect(() => {
    localStorage.setItem("sound-enabled", JSON.stringify(isSoundEnabled));
  }, [isSoundEnabled]);

  useEffect(() => {
    if (hasUserInteracted) return;

    const handleFirstInteraction = () => {
      setHasUserInteracted(true);

      if (isMusicEnabledRef.current) {
        backgroundMusic.play();
      }
    };

    document.addEventListener("click", handleFirstInteraction, { once: true });
    document.addEventListener("keydown", handleFirstInteraction, {
      once: true,
    });
    document.addEventListener("touchstart", handleFirstInteraction, {
      once: true,
    });

    return () => {
      document.removeEventListener("click", handleFirstInteraction);
      document.removeEventListener("keydown", handleFirstInteraction);
      document.removeEventListener("touchstart", handleFirstInteraction);
    };
  }, [hasUserInteracted, backgroundMusic]);

  useEffect(() => {
    if (!hasUserInteracted) return;

    if (isMusicEnabled) {
      backgroundMusic.play();
    } else {
      backgroundMusic.pause();
    }
  }, [isMusicEnabled, hasUserInteracted, backgroundMusic]);

  const value: AudioContextType = {
    isMusicEnabled,
    setMusicEnabled,
    isSoundEnabled,
    setSoundEnabled,
    playClickSound,
  };

  return (
    <AudioContext.Provider value={value}>{children}</AudioContext.Provider>
  );
}
